import { assign, sendParent } from '@monofront/xstate-v4';
import { SELECT_SHIPPING_METHOD_TRANSITIONS } from '../transitions';
import { SELECT_TIME_SLOTS_STATES } from './states';
import {
  ISelectDayEvent,
  ISelectHourEvent,
  ISelectTimeSlotsContext,
  ISelectTimeSlotsListResponseEvent,
} from './types';

// Courier slots: lego only sets an id on Woop slots. Slots from
// time-slot-planner (USE_WOOP_DELIVERY_SLOTS off for the method) have
// none, so the id cannot be required for them.
const isTimeSlotIdRequired = (context: ISelectTimeSlotsContext) =>
  context.timeSlots.some(({ slots }) => slots.some(({ id }) => Boolean(id)));

export const sendSelectedTimeSlotToParent = sendParent<
  ISelectTimeSlotsContext,
  ISelectHourEvent
>((context) => ({
  type: SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_TIME_SLOT,
  timeSlotSelected: {
    deliveryDate: context.daySelected,
    timeSlot: context.hourSelected,
    timeSlotId: context.timeSlotId,
  },
  isTimeSlotIdRequired: isTimeSlotIdRequired(context),
}));

const sendSelectedTimeSlotFromDayChangedToParent = sendParent<
  ISelectTimeSlotsContext,
  ISelectDayEvent
>((context, event) => ({
  type: SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_TIME_SLOT,
  timeSlotSelected: {
    deliveryDate: event.daySelected,
    timeSlot: context.hourSelected,
    timeSlotId: context.timeSlotId,
  },
  isTimeSlotIdRequired: isTimeSlotIdRequired(context),
}));

export const assignSelectDay = assign<ISelectTimeSlotsContext, ISelectDayEvent>(
  {
    daySelected: (context, event) => event.daySelected ?? context.daySelected,
    hourSelected: (_, event) => event.hourSelected ?? null,
    timeSlotId: (_, event) => event.timeSlotId ?? null,
  },
);

export const assignSelectHour = assign<
  ISelectTimeSlotsContext,
  ISelectHourEvent
>({
  timeSlotId: (context, event) => event.timeSlotId ?? context.timeSlotId,
  hourSelected: (context, event) => event.hourSelected ?? context.hourSelected,
});

export const assignClearSelection = assign<ISelectTimeSlotsContext, any>({
  daySelected: () => null,
  hourSelected: () => null,
  timeSlotId: () => null,
});

// The selection restored from the cart, or kept from another shipping method,
// carries no id valid for this list: take the id of the slot at the selected
// day and hour, and drop the hour when this list has no such slot.
export const assignSlotAvailabilitiesReceived = assign<
  ISelectTimeSlotsContext,
  ISelectTimeSlotsListResponseEvent
>((context, event) => {
  const timeSlots = event.data.data.getSlotAvailabilitiesV2;
  const selectedSlot = timeSlots
    .find(({ date }) => date === context.daySelected)
    ?.slots.find(({ value }) => value === context.hourSelected);

  return {
    timeSlots,
    hourSelected: selectedSlot?.value ?? null,
    timeSlotId: selectedSlot?.id ?? null,
  };
});

export const selectDayTransition = {
  target: SELECT_TIME_SLOTS_STATES.DAY_SELECTED,
  actions: [assignSelectDay, sendSelectedTimeSlotFromDayChangedToParent],
};

export const selectHourTransition = {
  target: SELECT_TIME_SLOTS_STATES.HOUR_SELECTED,
  actions: [assignSelectHour],
};

export const clearSelectionTransition = {
  target: SELECT_TIME_SLOTS_STATES.GET_SUCCESS,
  actions: [assignClearSelection],
};
